import { getAttendanceDisplayValue } from '@/components/feedbacks/session-feedback-utils.js';
import { stringIfNull } from '@/lib/string-utils.js';
import { FeedbacksTableColumnId } from '@/routes/_layout/sessions/-components/feedbacks-page-columns.js';
import {
  formatInstant,
  formatTimeRange,
  differenceInTimeUnit,
  isStudentRole,
  FrequentTimeZones,
  isWithinN,
  CsvCellValue,
} from '@airreading/common';
import { SessionFeedbackWithAssessment, studentYearOptionOnlyNumber } from '@airreading/common/data';

export const getFeedbacksTableOverrides = (
  schoolIdToDistrictName: Record<string, string>,
): Partial<Record<FeedbacksTableColumnId, (row: SessionFeedbackWithAssessment, index: number) => CsvCellValue>> => {
  const timeZone = FrequentTimeZones.UsCentral;

  return {
    date: ({ session }) => formatInstant(session.startInstant, timeZone).formattedDate,
    time: ({ session }) => formatTimeRange(session.startInstant, session.endInstant, timeZone),
    student: ({ student }) => student.fullNameOrEmail,
    sessionTeacher: ({ coach }) => coach.fullNameOrEmail,
    originalTeacher: ({ coach }) => coach?.fullNameOrEmail || '-',
    attendance: (row) => getAttendanceDisplayValue(row),
    feedbackStatus: (row) => {
      if (!row) return '-';

      const { session, feedbackSubmitted, lateFeedbackDismissed, submittedAtInstant } = row;

      const endInstant = session.endInstant;
      const allSubmitted = feedbackSubmitted;
      const allLateFeedbacksDismissed = lateFeedbackDismissed;

      const isCanceledWithin12h = isWithinN(session.hoursCanceledBeforeStart, 12);

      if (!allSubmitted && !isCanceledWithin12h) return '-';

      const latestSubmittedAt = submittedAtInstant != null ? submittedAtInstant : undefined;

      const moreThan24H =
        !allLateFeedbacksDismissed &&
        latestSubmittedAt != null &&
        differenceInTimeUnit(endInstant, latestSubmittedAt, 'hour') >= 24;

      return `Sent ${moreThan24H ? '(> 24H)' : ''}`;
    },
    assessmentFinished: ({ assessmentFinished }) => {
      if (assessmentFinished == null) return '-';
      return assessmentFinished ? 'Completed' : 'Incomplete';
    },
    district: ({ student }) => {
      const schoolId = student.userRoles.find(isStudentRole)?.school?.id;
      const districtName = schoolId ? schoolIdToDistrictName[schoolId] : null;
      return districtName || 'Unknown';
    },
    school: ({ student }) => {
      const schoolName = student.userRoles.find(isStudentRole)?.school?.name;
      return schoolName || 'Unknown';
    },
    grade: ({ student }) => {
      const grade = student?.studentProfile?.grade;
      return studentYearOptionOnlyNumber(stringIfNull(grade));
    },
    lessonName: ({ session }) => (session?.lesson?.name ? `'${session.lesson.name}'` : 'Unknown'),
    recordingLink: ({ session }) => {
      const recordingUrls = session?.zoom?.recordingUrls ?? [];
      return recordingUrls.length > 0 ? recordingUrls.join(', ') : '';
    },
    studentIssues: ({ issues }) => issues.join(', '),
    majorClassConcerns: ({ issues }) => issues.join(', '),
    majorConcernsText: ({ majorConcerns }) => majorConcerns,
    gradeLevelFluencyWPM: ({ gradeLevelWPM }) => (gradeLevelWPM !== null ? gradeLevelWPM.toFixed(2) : '-'),
    gradeLevelAccuracy: ({ gradeLevelAccuracy }) =>
      gradeLevelAccuracy !== null ? `${gradeLevelAccuracy.toFixed(2)}%` : '-',
    progressMonitorWPM: ({ progressMonitorWPM }) => (progressMonitorWPM !== null ? progressMonitorWPM.toFixed(2) : '-'),
    progressMonitorAccuracy: ({ progressMonitorAccuracy }) =>
      progressMonitorAccuracy !== null ? `${progressMonitorAccuracy.toFixed(2)}%` : '-',
  };
};
